import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  rollUpStage,
  type BulkStageMoveResult,
  type GarmentStageFlowView,
  type KanbanBoard,
  type KanbanCard,
  type StageConfigurationView,
  type StageHistory,
  type StageMoveResult,
  type WorkflowStage,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import {
  useQueueableMutation,
  type QueueableMutationResult,
} from '../offline/use-queueable-mutation.js';
import {
  bulkAdvance,
  deliverOrder,
  getBoard,
  getItemHistory,
  getStageConfiguration,
  stageMovePath,
  updateStageFlows,
  updateStageSettings,
  type BoardParams,
  type StageSettingInput,
} from './kanban-api.js';

/**
 * The board's data layer, and the one piece of real behaviour in it: the optimistic move.
 *
 * A card must move the instant it is dropped — a shop moving forty cards at the end of the day
 * cannot wait for a round trip each time, and on a shop's connection that round trip is often
 * seconds. So the card is moved in the cache first, and one of three things follows:
 *
 *   - the server agrees, and the board is refreshed from it;
 *   - the server refuses (someone else moved it, the stage is disabled, the shop skipped a
 *     stage), and the SNAPSHOT taken before the move is put back — the card visibly returns to
 *     where it came from, and the caller shows the server's reason;
 *   - there is no connection, and the write goes to task 5's durable queue. Here the optimistic
 *     move is KEPT, because the write is not lost, only late: it will be delivered in order on
 *     reconnect. Reverting the card would tell the shop their work had not been recorded, which
 *     is the opposite of the truth.
 */

export const kanbanKeys = {
  all: ['kanban'] as const,
  board: (params: BoardParams) => ['kanban', 'board', params] as const,
  stages: ['kanban', 'stages'] as const,
  history: (itemId: string) => ['kanban', 'history', itemId] as const,
};

export function useKanbanBoard(params: BoardParams): UseQueryResult<KanbanBoard> {
  const client = useApiClient();
  return useQuery({
    queryKey: kanbanKeys.board(params),
    queryFn: () => getBoard(client, params),
    // Keeps the previous board on screen while a filter change loads, so columns do not blink.
    placeholderData: (previous) => previous,
  });
}

/** This shop's stage names and workflow. Rarely changes, so it is cached hard. */
export function useStageConfiguration(): UseQueryResult<StageConfigurationView> {
  const client = useApiClient();
  return useQuery({
    queryKey: kanbanKeys.stages,
    queryFn: () => getStageConfiguration(client),
    staleTime: 5 * 60 * 1000,
  });
}

export function useItemHistory(
  orderId: string | null,
  itemId: string | null,
): UseQueryResult<StageHistory> {
  const client = useApiClient();
  return useQuery({
    queryKey: kanbanKeys.history(itemId ?? ''),
    queryFn: () => getItemHistory(client, orderId as string, itemId as string),
    enabled: orderId !== null && itemId !== null,
  });
}

export interface StageMoveVariables {
  card: KanbanCard;
  toStage: WorkflowStage;
  reason?: string | null;
  rework?: boolean;
}

/** Move a card between columns in a cached board, leaving every other board untouched. */
function boardWithCardMoved(
  board: KanbanBoard,
  card: KanbanCard,
  toStage: WorkflowStage,
): KanbanBoard {
  if (!board.columns.some((column) => column.stage === toStage)) return board;

  const moved: KanbanCard = { ...card, stage: toStage };
  return {
    ...board,
    columns: board.columns.map((column) => {
      if (column.stage === card.stage) {
        const cards = column.cards.filter((one) => one.itemId !== card.itemId);
        return { ...column, cards, total: Math.max(0, column.total - 1) };
      }
      if (column.stage === toStage) {
        return { ...column, cards: [moved, ...column.cards], total: column.total + 1 };
      }
      return column;
    }),
  };
}

export interface StageMoveContext {
  /** Every board query as it was before the move, for an exact rollback. */
  snapshots: [readonly unknown[], KanbanBoard | undefined][];
}

export function useMoveItemStage() {
  const queryClient = useQueryClient();

  return useQueueableMutation<StageMoveResult, StageMoveVariables>({
    buildRequest: (variables) => ({
      method: 'POST',
      path: stageMovePath(variables.card.orderId, variables.card.itemId),
      body: {
        toStage: variables.toStage,
        ...(variables.reason ? { reason: variables.reason } : {}),
        ...(variables.rework ? { rework: true } : {}),
        // Sent on every move, so a stale board and a replayed queued write both land as a
        // visible conflict rather than a silent double advance.
        expectedStage: variables.card.stage,
      },
      labelKey: 'kanban.queue.stageMove',
      labelParams: { garment: variables.card.garmentTypeLabel, order: variables.card.orderNumber },
      invalidate: [[...kanbanKeys.all], ['orders']],
    }),

    onMutate: async (variables): Promise<StageMoveContext> => {
      // Stop any board refetch in flight, or it could land after the optimistic write and
      // undo it for no reason.
      await queryClient.cancelQueries({ queryKey: kanbanKeys.all });

      const snapshots = queryClient.getQueriesData<KanbanBoard>({ queryKey: ['kanban', 'board'] });
      for (const [key, board] of snapshots) {
        if (!board) continue;
        queryClient.setQueryData(key, boardWithCardMoved(board, variables.card, variables.toStage));
      }
      return { snapshots: snapshots.map(([key, board]) => [key, board]) };
    },

    onError: (_error, _variables, context) => {
      // Visibly back where it came from. The caller shows the server's reason.
      for (const [key, board] of (context as StageMoveContext | undefined)?.snapshots ?? []) {
        queryClient.setQueryData(key, board);
      }
    },

    onSuccess: (result: QueueableMutationResult<StageMoveResult>) => {
      // A queued write keeps the optimistic board: refetching now would show the shop the
      // server's older answer and make their work look lost.
      if (result.queued) return;
      void queryClient.invalidateQueries({ queryKey: kanbanKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

/**
 * The end-of-day actions. Neither is queueable: both are multi-garment writes whose per-garment
 * outcome (`skipped`) is the whole point, and a shop cannot act on "six moved, two refused"
 * that arrives an hour later. They fail loudly instead, and the shop retries.
 */
export function useBulkAdvance() {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<BulkStageMoveResult, unknown, string[]>({
    mutationFn: (itemIds) => bulkAdvance(client, itemIds),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: kanbanKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

export function useDeliverOrder() {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<BulkStageMoveResult, unknown, { orderId: string; itemIds?: string[] }>({
    mutationFn: (variables) => deliverOrder(client, variables.orderId, variables.itemIds),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: kanbanKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

/**
 * What an order's stage would become if these cards moved — the same shared rollup the server
 * uses, so a selection's effect on the bill can be previewed without asking.
 */
export function previewOrderStage(cards: readonly KanbanCard[]): WorkflowStage {
  return rollUpStage(cards.map((card) => card.stage));
}

/**
 * Save which stages each garment type walks.
 *
 * Invalidates the whole `kanban` key rather than just the stage configuration: the board's
 * cards carry their garment type, and the controls they offer are computed from these flows,
 * so a board left open beside the settings screen must not keep offering the old route.
 */
export function useUpdateStageFlows(): UseMutationResult<
  StageConfigurationView,
  Error,
  readonly GarmentStageFlowView[]
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (flows: readonly GarmentStageFlowView[]) => updateStageFlows(client, flows),
    onSuccess: (configuration) => {
      queryClient.setQueryData(kanbanKeys.stages, configuration);
      void queryClient.invalidateQueries({ queryKey: kanbanKeys.all });
    },
  });
}

export function useUpdateStageSettings(): UseMutationResult<
  StageConfigurationView,
  Error,
  readonly StageSettingInput[]
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (stages: readonly StageSettingInput[]) => updateStageSettings(client, stages),
    onSuccess: (configuration) => {
      queryClient.setQueryData(kanbanKeys.stages, configuration);
      void queryClient.invalidateQueries({ queryKey: kanbanKeys.all });
    },
  });
}
